import type { AppointmentPolicy } from "@/api/types";

/**
 * Appointments are picked on the store's wall clock: dates and times here are the store's
 * ("2026-10-14", "17:30"), never the browser's. (Same as dealer-web's src/lib/appointments.ts.)
 */

export const WEEKDAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;
export type Weekday = (typeof WEEKDAYS)[number];
export const WEEKDAY_LABEL: Record<Weekday, string> = {
  mon: "Monday",
  tue: "Tuesday",
  wed: "Wednesday",
  thu: "Thursday",
  fri: "Friday",
  sat: "Saturday",
  sun: "Sunday",
};
export interface DayHours {
  open: boolean;
  start: string;
  end: string;
}

export const DEFAULT_HOURS: Record<Weekday, DayHours> = {
  mon: { open: true, start: "09:00", end: "19:00" },
  tue: { open: true, start: "09:00", end: "19:00" },
  wed: { open: true, start: "09:00", end: "19:00" },
  thu: { open: true, start: "09:00", end: "19:00" },
  fri: { open: true, start: "09:00", end: "19:00" },
  sat: { open: true, start: "10:00", end: "17:00" },
  sun: { open: false, start: "10:00", end: "17:00" },
};
export const DEFAULT_MESSAGE =
  "You're all set for {{date_time}} at {{store_name}}, {{store_address}}, for your {{year}} {{make}} {{model}}. When you arrive, ask for {{ask_for}}. {{note}}";

/** The variables of the appointment message, as offered after typing "#". */
export const MESSAGE_VARIABLES = [
  { key: "year", label: "Year", sample: "2019" },
  { key: "make", label: "Make", sample: "Toyota" },
  { key: "model", label: "Model", sample: "Camry" },
  { key: "store_name", label: "Store name", sample: "ABC Toyota" },
  { key: "store_address", label: "Store address", sample: "1200 Main St, Austin, TX 78701" },
  { key: "date_time", label: "Date and time", sample: "Tue, Oct 14 at 5:30 PM" },
  { key: "note", label: "Note", sample: "Please bring your title, both keys and a photo ID." },
  { key: "ask_for", label: "Ask for", sample: "the buying team" },
] as const;
export type MessageVariable = (typeof MESSAGE_VARIABLES)[number]["key"];

export type AppointmentSettings = Required<AppointmentPolicy>;

const DEFAULT_POLICY: AppointmentPolicy = { aiCanSchedule: false, minLeadMinutes: 60, maxDaysAhead: 30 };

/** The store's appointment settings, with defaults for older servers. */
export function appointmentSettings(policy: { appointments?: AppointmentPolicy }): AppointmentSettings {
  const a = policy.appointments ?? DEFAULT_POLICY;
  return {
    aiCanSchedule: a.aiCanSchedule,
    minLeadMinutes: a.minLeadMinutes,
    maxDaysAhead: a.maxDaysAhead,
    hours: a.hours ?? DEFAULT_HOURS,
    slotMinutes: a.slotMinutes ?? 30,
    note: a.note ?? "",
    askFor: a.askFor ?? "the buying team",
    messageTemplate: a.messageTemplate ?? DEFAULT_MESSAGE,
  };
}

const pad = (n: number) => String(n).padStart(2, "0");
const toMinutes = (hhmm: string) => Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3, 5));
const toHhmm = (m: number) => `${pad(Math.floor(m / 60))}:${pad(m % 60)}`;

/** The store's wall clock now. */
export function storeNow(timeZone: string, now: Date = new Date()): { date: string; time: string } {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).formatToParts(now);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "00";
  return { date: `${get("year")}-${get("month")}-${get("day")}`, time: `${pad(Number(get("hour")) % 24)}:${get("minute")}` };
}

/** "2026-10-14" + n days. */
export function addDays(date: string, n: number): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

export function weekdayOf(date: string): Weekday {
  return (["sun", "mon", "tue", "wed", "thu", "fri", "sat"] as const)[new Date(`${date}T00:00:00Z`).getUTCDay()] as Weekday;
}

/** Start times of the slots a day offers (a whole slot fits before closing). */
export function daySlots(settings: AppointmentSettings, date: string): string[] {
  const day = settings.hours[weekdayOf(date)];
  if (!day?.open) return [];
  const out: string[] = [];
  for (let m = toMinutes(day.start); m + settings.slotMinutes <= toMinutes(day.end); m += settings.slotMinutes) out.push(toHhmm(m));
  return out;
}

/** Slots still bookable: today's past ones are gone. */
export function openSlots(settings: AppointmentSettings, date: string, now: { date: string; time: string }): string[] {
  const slots = daySlots(settings, date);
  if (date < now.date) return [];
  return date === now.date ? slots.filter((t) => t > now.time) : slots;
}

/** "17:30" → "5:30 PM". */
export function formatSlot(time: string): string {
  const m = toMinutes(time);
  const h = Math.floor(m / 60);
  return `${h % 12 === 0 ? 12 : h % 12}:${pad(m % 60)} ${h < 12 ? "AM" : "PM"}`;
}

/** "Tue, Oct 14 at 5:30 PM" for a store-local date and time (as the seller's text says it). */
export function formatStoreSlot(date: string, time: string): string {
  const day = new Intl.DateTimeFormat("en-US", { timeZone: "UTC", weekday: "short", month: "short", day: "numeric" }).format(new Date(`${date}T00:00:00Z`));
  return `${day} at ${formatSlot(time)}`;
}

/** The text with `{{variables}}` filled in (empty ones vanish with the space or comma they leave). */
export function renderTemplate(template: string, values: Partial<Record<MessageVariable, string>>): string {
  return template
    .replace(/\{\{\s*([a-z_]+)\s*\}\}/g, (match, name: string) => (name in values ? (values[name as MessageVariable] ?? "") : match))
    .replace(/[ \t]+/g, " ")
    .replace(/ ([,.!?;:])/g, "$1")
    .replace(/,([,.])/g, "$1")
    .trim();
}

/** Variables in a template that don't exist. */
export function unknownVariables(template: string): string[] {
  const known = new Set<string>(MESSAGE_VARIABLES.map((v) => v.key));
  return [...template.matchAll(/\{\{\s*([a-z_]+)\s*\}\}/g)].map((m) => m[1] as string).filter((v) => !known.has(v));
}

/** What's wrong with appointment settings, keyed like the server's field errors ("appointments.hours.mon.end"). */
export function appointmentPolicyErrors(a: AppointmentPolicy | undefined): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!a) return errors;
  for (const day of WEEKDAYS) {
    const h = a.hours?.[day];
    if (h?.open && h.start >= h.end) errors[`appointments.hours.${day}.end`] = "Opening must be before closing";
  }
  if (!(Number.isInteger(a.minLeadMinutes) && a.minLeadMinutes >= 0)) errors["appointments.minLeadMinutes"] = "Enter whole minutes (0 or more)";
  if (!(Number.isInteger(a.maxDaysAhead) && a.maxDaysAhead >= 1)) errors["appointments.maxDaysAhead"] = "Enter whole days (1 or more)";
  if (a.slotMinutes !== undefined && !(a.slotMinutes >= 15 && a.slotMinutes <= 120 && a.slotMinutes % 15 === 0))
    errors["appointments.slotMinutes"] = "15 to 120 minutes, in steps of 15";
  if (a.messageTemplate !== undefined) {
    const unknown = unknownVariables(a.messageTemplate);
    if (!a.messageTemplate.trim()) errors["appointments.messageTemplate"] = "Write the message";
    else if (unknown.length) errors["appointments.messageTemplate"] = `Unknown variable: ${unknown.map((v) => `{{${v}}}`).join(", ")}`;
  }
  return errors;
}

/** The instant of a store-local date and time (for the mock backend; the real server does this). */
export function storeLocalToInstant(date: string, time: string, timeZone: string): Date {
  const [y, mo, d] = date.split("-").map(Number) as [number, number, number];
  const [h, mi] = time.split(":").map(Number) as [number, number];
  let guess = Date.UTC(y, mo - 1, d, h, mi);
  for (let i = 0; i < 2; i++) {
    const local = storeNow(timeZone, new Date(guess));
    const [ly, lmo, ld] = local.date.split("-").map(Number) as [number, number, number];
    const [lh, lmi] = local.time.split(":").map(Number) as [number, number];
    guess += Date.UTC(y, mo - 1, d, h, mi) - Date.UTC(ly, lmo - 1, ld, lh, lmi);
  }
  return new Date(guess);
}
